import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";
import EmailImageUpload from "../EmailImageUpload";
import { getImageUrl } from "../../../utils/imageHelper";

/**
 * Ecom Ingredients Component
 * Displays a carousel of ingredient cards with circular icons, titles, descriptions, and nutrition benefits
 * Shows 2 cards at a time with navigation arrows
 */
const EcomIngredients = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    title = "",
    description = "",
    ingredients = [],
    component_name = "ecom_ingredients",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [localDescription, setLocalDescription] = useState(description);
  const [localIngredients, setLocalIngredients] = useState(
    Array.isArray(ingredients) && ingredients.length > 0
      ? ingredients
      : [
          {
            icon: null,
            title: "Tamarind pulp",
            description:
              "Tamarind pulp is made from the fruit of the tamarind tree and is a staple in Indian cuisine. It offers a deliciously tangy flavor while being packed with antioxidants, vitamins, and minerals.",
            benefitsHeading: "Nutrition Benefits:",
            benefits:
              "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
          },
          {
            icon: null,
            title: "Tamarind pulp",
            description:
              "Tamarind pulp is made from the fruit of the tamarind tree and is a staple in Indian cuisine. It offers a deliciously tangy flavor while being packed with antioxidants, vitamins, and minerals.",
            benefitsHeading: "Nutrition Benefits:",
            benefits:
              "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
          },
        ]
  );

  // For email compatibility, show all ingredients in preview mode (no carousel)
  // In edit mode, we can show carousel for better UX
  const itemsPerView = previewMode ? localIngredients.length : 2; // Show all in preview, 2 in edit
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setLocalTitle(title || "");
    setLocalDescription(description || "");
    if (Array.isArray(ingredients) && ingredients.length > 0) {
      setLocalIngredients(ingredients);
    }
  }, [id, title, description, ingredients]);

  const handleSave = () => {
    updateSection(id, {
      id,
      component_name,
      title: localTitle,
      description: localDescription,
      ingredients: localIngredients,
    });
    setIsEditing(false);
  };

  const handleIngredientUpdate = (index, field, value) => {
    const updated = [...localIngredients];
    updated[index] = { ...updated[index], [field]: value };
    setLocalIngredients(updated);
  };

  const addIngredient = () => {
    setLocalIngredients([
      ...localIngredients,
      {
        icon: null,
        title: "New Ingredient",
        description: "Enter ingredient description here.",
        benefitsHeading: "Nutrition Benefits:",
        benefits: "Enter nutrition benefits here.",
      },
    ]);
  };

  const removeIngredient = (index) => {
    const newIngredients = localIngredients.filter((_, i) => i !== index);
    setLocalIngredients(newIngredients);
    if (currentIndex >= newIngredients.length - itemsPerView && currentIndex > 0) {
      setCurrentIndex(Math.max(0, currentIndex - 1));
    }
  };

  const nextSlide = () => {
    if (previewMode) return; // Disable in preview mode
    const maxIndex = Math.max(0, localIngredients.length - itemsPerView);
    setCurrentIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
  };

  const prevSlide = () => {
    if (previewMode) return; // Disable in preview mode
    const maxIndex = Math.max(0, localIngredients.length - itemsPerView);
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component ecom-ingredients-editor p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit Ingredients Section</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Title</label>
          <input
            className="form-control"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder="Enter section title"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <textarea
            className="form-control"
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            placeholder="Enter description"
            rows={2}
          />
        </div>

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label fw-bold mb-0">Ingredients</label>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={addIngredient}
            >
              Add Ingredient
            </button>
          </div>

          {localIngredients.map((ingredient, index) => (
            <div
              key={index}
              className="border rounded p-3 mb-3"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Ingredient {index + 1}</strong>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => removeIngredient(index)}
                >
                  Remove
                </button>
              </div>

              <div className="mb-2">
                <label className="form-label small">Icon/Image (Circular)</label>
                <EmailImageUpload
                  initialImage={ingredient.icon}
                  onImageUpload={(img) => {
                    handleIngredientUpdate(index, "icon", img);
                  }}
                />
              </div>

              <div className="mb-2">
                <label className="form-label small">Title</label>
                <input
                  className="form-control form-control-sm"
                  value={ingredient.title || ""}
                  onChange={(e) =>
                    handleIngredientUpdate(index, "title", e.target.value)
                  }
                  placeholder="Tamarind pulp"
                />
              </div>

              <div className="mb-2">
                <label className="form-label small">Description</label>
                <textarea
                  className="form-control form-control-sm"
                  value={ingredient.description || ""}
                  onChange={(e) =>
                    handleIngredientUpdate(index, "description", e.target.value)
                  }
                  placeholder="Enter ingredient description"
                  rows={3}
                />
              </div>

              <div className="mb-2">
                <label className="form-label small">Benefits Heading</label>
                <input
                  className="form-control form-control-sm"
                  value={ingredient.benefitsHeading || "Nutrition Benefits:"}
                  onChange={(e) =>
                    handleIngredientUpdate(index, "benefitsHeading", e.target.value)
                  }
                  placeholder="Nutrition Benefits:"
                />
              </div>

              <div>
                <label className="form-label small">Benefits Text</label>
                <textarea
                  className="form-control form-control-sm"
                  value={ingredient.benefits || ""}
                  onChange={(e) =>
                    handleIngredientUpdate(index, "benefits", e.target.value)
                  }
                  placeholder="Enter nutrition benefits"
                  rows={2}
                />
              </div>
            </div>
          ))}

          {localIngredients.length === 0 && (
            <div className="text-center p-4 border rounded" style={{ backgroundColor: "#f8f9fa" }}>
              <p className="text-muted mb-2">No ingredients yet</p>
              <button className="btn btn-sm btn-primary" onClick={addIngredient}>
                Add First Ingredient
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const { primaryColor, secondaryColor, fontFamily, backgroundColor } =
    styleSettings || {};

  // In preview mode, show all ingredients (email compatible - no JavaScript)
  // In edit mode, show carousel with navigation
  const visibleIngredients = previewMode
    ? localIngredients
    : localIngredients.slice(currentIndex, currentIndex + itemsPerView);

  const maxIndex = Math.max(0, localIngredients.length - itemsPerView);
  const canGoNext = !previewMode && localIngredients.length > itemsPerView && currentIndex < maxIndex;
  const canGoPrev = !previewMode && currentIndex > 0;

  return (
    <>
      {!previewMode && (
        <div className="email-component ecom-ingredients mb-3">
          <div className="component-controls mb-2 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
          backgroundColor: backgroundColor || "#ffffff",
        }}
      >
        <tr>
          <td
            align="center"
            style={{
              padding: "32px 16px",
              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
            }}
          >
            {/* Title */}
            {localTitle && (
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "bold",
                  color: primaryColor || "#333333",
                  marginBottom: "16px",
                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                }}
              >
                {localTitle}
              </div>
            )}

            {/* Description */}
            {localDescription && (
              <div
                style={{
                  fontSize: "16px",
                  color: primaryColor || "#333333",
                  marginBottom: "32px",
                  lineHeight: "1.6",
                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                }}
              >
                {localDescription}
              </div>
            )}

            {/* Ingredients Carousel */}
            {localIngredients.length > 0 ? (
              <>
                <table
                  width="100%"
                  cellPadding="0"
                  cellSpacing="0"
                  border="0"
                  style={{
                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                    marginBottom: "24px",
                  }}
                >
                  <tr>
                    {visibleIngredients.map((ingredient, idx) => {
                      const actualIndex = currentIndex + idx;
                      const iconUrl = getImageUrl(ingredient.icon);

                      return (
                        <td
                          key={actualIndex}
                          width={previewMode ? "50%" : "50%"}
                          valign="top"
                          style={{
                            padding: "0 8px",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <table
                            width="100%"
                            cellPadding="0"
                            cellSpacing="0"
                            border="0"
                            style={{
                              backgroundColor: "#ffffff",
                              border: "1px solid #e0e0e0",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            }}
                          >
                            <tr>
                              <td
                                align="center"
                                style={{
                                  padding: "24px 24px 16px 24px",
                                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                }}
                              >
                                {/* Circular Icon */}
                                <table
                                  cellPadding="0"
                                  cellSpacing="0"
                                  border="0"
                                  align="center"
                                  style={{
                                    width: "80px",
                                    height: "80px",
                                    backgroundColor: "#f0f0f0",
                                    border: "2px solid #ff6b6b",
                                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                  }}
                                >
                                  <tr>
                                    <td
                                      align="center"
                                      valign="middle"
                                      style={{
                                        width: "80px",
                                        height: "80px",
                                        fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                      }}
                                    >
                                      {iconUrl ? (
                                        <img
                                          src={iconUrl}
                                          alt={ingredient.title || "Ingredient"}
                                          width="76"
                                          height="76"
                                          style={{
                                            width: "76px",
                                            height: "76px",
                                            display: "block",
                                            borderRadius: "50%",
                                          }}
                                        />
                                      ) : (
                                        <div
                                          style={{
                                            width: "40px",
                                            height: "40px",
                                            backgroundColor: "#ff6b6b",
                                            borderRadius: "50%",
                                            margin: "0 auto",
                                          }}
                                        />
                                      )}
                                    </td>
                                  </tr>
                                </table>
                              </td>
                            </tr>

                            {/* Title */}
                            <tr>
                              <td
                                align="center"
                                style={{
                                  padding: "0 24px 12px 24px",
                                  fontSize: "18px",
                                  fontWeight: "bold",
                                  color: primaryColor || "#333333",
                                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                }}
                              >
                                {ingredient.title || "Ingredient Title"}
                              </td>
                            </tr>

                            {/* Description */}
                            {ingredient.description && (
                              <tr>
                                <td
                                  style={{
                                    padding: "0 24px 16px 24px",
                                    fontSize: "14px",
                                    color: primaryColor || "#666666",
                                    lineHeight: "1.6",
                                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                  }}
                                >
                                  {ingredient.description}
                                </td>
                              </tr>
                            )}

                            {/* Benefits Heading */}
                            {ingredient.benefitsHeading && (
                              <tr>
                                <td
                                  style={{
                                    padding: "0 24px 8px 24px",
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    color: primaryColor || "#333333",
                                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                  }}
                                >
                                  {ingredient.benefitsHeading}
                                </td>
                              </tr>
                            )}

                            {/* Benefits Text */}
                            {ingredient.benefits && (
                              <tr>
                                <td
                                  style={{
                                    padding: "0 24px 24px 24px",
                                    fontSize: "14px",
                                    color: primaryColor || "#666666",
                                    lineHeight: "1.6",
                                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                  }}
                                >
                                  {ingredient.benefits}
                                </td>
                              </tr>
                            )}
                          </table>
                        </td>
                      );
                    })}
                  </tr>
                </table>

                {/* Navigation Arrows - Only show in edit mode, not in preview/email */}
                {!previewMode && localIngredients.length > itemsPerView && (
                  <table
                    width="100%"
                    cellPadding="0"
                    cellSpacing="0"
                    border="0"
                    style={{
                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                      marginTop: "16px",
                    }}
                  >
                    <tr>
                      <td align="center">
                        <table
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <tr>
                            <td
                              align="center"
                              style={{
                                padding: "0 8px",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  prevSlide();
                                }}
                                style={{
                                  display: "inline-block",
                                  width: "40px",
                                  height: "40px",
                                  border: "1px solid #e0e0e0",
                                  backgroundColor: canGoPrev ? "#ffffff" : "#f5f5f5",
                                  textDecoration: "none",
                                  textAlign: "center",
                                  lineHeight: "40px",
                                  fontSize: "18px",
                                  color: primaryColor || "#333333",
                                  opacity: canGoPrev ? 1 : 0.5,
                                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                }}
                              >
                                ←
                              </a>
                            </td>
                            <td
                              align="center"
                              style={{
                                padding: "0 8px",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  nextSlide();
                                }}
                                style={{
                                  display: "inline-block",
                                  width: "40px",
                                  height: "40px",
                                  border: "1px solid #e0e0e0",
                                  backgroundColor: canGoNext ? "#ffffff" : "#f5f5f5",
                                  textDecoration: "none",
                                  textAlign: "center",
                                  lineHeight: "40px",
                                  fontSize: "18px",
                                  color: primaryColor || "#333333",
                                  opacity: canGoNext ? 1 : 0.5,
                                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                }}
                              >
                                →
                              </a>
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>
                )}
              </>
            ) : (
              <div
                style={{
                  padding: "24px",
                  textAlign: "center",
                  color: primaryColor || "#999999",
                  fontStyle: "italic",
                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                }}
              >
                No ingredients available
              </div>
            )}
          </td>
        </tr>
      </table>
    </>
  );
};

export default EcomIngredients;

